/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { Role } from '@lms-types/index';

/**
 * The single source of truth for role display names (code-health plan item
 * 1.6). The live walkthrough found the same role spelled four ways at once —
 * "Organization Admin" (Settings), "Org Admin" (header badge), "Admin"
 * (Users & Groups selects) and raw "org_admin" (Users tab) — which reads as
 * four different roles to anyone who doesn't know the enum. Change a label
 * here and it changes everywhere.
 */
export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  instructor: 'Instructor',
  ta: 'Teaching Assistant',
  learner: 'Learner',
};

/**
 * Display name for a role. Unknown values (a future enum member arriving
 * from the API before the frontend learns it) degrade to Title Case rather
 * than leaking the raw snake_case token.
 */
export function formatRole(role: string): string {
  return (
    ROLE_LABELS[role as Role] ?? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
